import React from 'react';
import { LegalPageShell, Section, Callout } from '../components/shared/LegalPageShell';

/**
 * MedicalDisclaimerPage — Wave G-B · /medical-disclaimer
 *
 * Calm, non-alarming statement of what Kindra is and isn't, written
 * in the same first-person-plural editorial voice as the other trust
 * surfaces. No legal hedge-speak; no false reassurance; no dramatics.
 */
const MedicalDisclaimerPage = () => (
  <LegalPageShell
    eyebrow="Medical disclaimer"
    title="What Kindra is — and what it isn't."
    subtitle="Kindra is a personal health companion. It explains, organises and remembers your health information so you walk into appointments better prepared. It is not a substitute for medical care."
    updated="30 April 2026"
    current="/medical-disclaimer"
  >
    <Section eyebrow="The short version" title="Read this first.">
      <p>
        Kindra helps you understand your reports, prepare smarter questions, and keep track of your health between appointments. It does not diagnose, prescribe, or replace your clinician. Always discuss decisions about treatment, medication, and care with a qualified health professional.
      </p>
    </Section>

    <Section title="What Kindra does">
      <ul className="space-y-2 list-disc pl-5">
        <li>Explains pathology, imaging and specialist letters in plain English.</li>
        <li>Tracks your conditions, medications, symptoms and notes over time.</li>
        <li>Summarises your recent context into a brief you can take to appointments.</li>
        <li>Suggests calm, sensible questions to raise with your doctor.</li>
        <li>Keeps your health story in one place so nothing important gets lost.</li>
      </ul>
    </Section>

    <Section title="What Kindra does not do">
      <ul className="space-y-2 list-disc pl-5">
        <li>It does not give a diagnosis. Numbers in a report can mean different things in different people; only your clinician has the full picture.</li>
        <li>It does not prescribe medication, change doses, or recommend you start or stop a treatment.</li>
        <li>It does not provide emergency care. If you are unwell now, please contact a clinician or call your local emergency number.</li>
        <li>It does not replace conversations with your GP, specialist, pharmacist or allied health team.</li>
      </ul>
    </Section>

    <Callout testid="disclaimer-emergency-callout">
      <p className="font-medium mb-2">If you're in an emergency, please don't use Kindra.</p>
      <p>
        Call <strong>000</strong> in Australia, or your local emergency number. If you're in emotional distress, Lifeline is available 24/7 on <strong>13 11 14</strong> (Australia).
      </p>
    </Callout>

    <Section title="How to use Kindra well">
      <p>
        Use Kindra to understand things first, then bring those questions to your doctor. The pattern that works best for the people we serve looks like this:
      </p>
      <ul className="space-y-2 list-disc pl-5">
        <li>Upload a recent report. Read Kindra's plain-English explanation.</li>
        <li>Add the conditions, medications and symptoms you're tracking.</li>
        <li>Ask Kindra anything you want to understand before your visit.</li>
        <li>Take the appointment brief Kindra prepares. Discuss it with your clinician.</li>
        <li>After the visit, add what your doctor said so Kindra can carry it forward.</li>
      </ul>
    </Section>

    <Section title="Accuracy and limits">
      <p>
        Kindra uses modern AI to read your reports and write its responses. We design it to be careful and conservative, but it is not infallible. Always cross-check anything important with a qualified clinician — and tell us at <a href="mailto:hello@kindraai.dev" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: '#0E1C2E' }}>hello@kindraai.dev</a> if something it said felt wrong, unsafe, or confusing.
      </p>
    </Section>

    <Section title="Where Kindra fits">
      <p>
        Think of Kindra as the calm hour you wish you had between leaving the clinic and your next appointment — a place to read what was said, look up what you didn't catch, and prepare what you want to ask next time. The clinical decisions stay where they belong: with your doctor, your specialist, and you.
      </p>
    </Section>
  </LegalPageShell>
);

export default MedicalDisclaimerPage;
